"use client";

import { useEffect, useId, useState } from "react";

import { usePathname, useRouter, useSearchParams } from "next/navigation";

import cn from "classnames";

import { Button } from "@/components/button/button.component";
import { Modal } from "@/components/modal/modalContainer.component";
import { RichText } from "@/components/richText/richText.component";

import { CONTENT_MODAL_QUERY_PARAM } from "@/constants/contentModal.constants";
import type { GetContentModalsQueryResult } from "@/sanity.types";

import { HowItWorksPanel } from "./howItWorksPanel.component";

export function ContentModalClient({ modals }: ContentModalClientProps) {
  const [dialog, setDialog] = useState<HTMLDialogElement | null>(null);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const modalId = searchParams.get(CONTENT_MODAL_QUERY_PARAM);
  const modal = modals.find((item) => item._id === modalId) ?? null;
  const titleId = `content-modal-${useId().replaceAll(":", "")}-title`;

  const close = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete(CONTENT_MODAL_QUERY_PARAM);
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false,
    });
  };

  useEffect(() => {
    if (modal && dialog && !dialog.open) {
      dialog.showModal();
    }
  }, [dialog, modal]);

  if (!modal) return null;

  return (
    <Modal
      ref={setDialog}
      aria-labelledby={titleId}
      onClose={close}
      className={cn(
        "overflow-y-auto rounded-3xl!",
        modal.layout === "withHowItWorks"
          ? "max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[1000px]! text-left"
          : "max-h-[88dvh] text-center min-[720px]:max-lg:w-[calc(100vw-4rem)] min-[720px]:max-lg:max-w-xl! lg:min-w-4xl",
      )}>
      <div className="flex flex-col gap-6 pb-2 min-[720px]:gap-10 min-[720px]:px-5 min-[720px]:pb-6">
        <section className="flex flex-col gap-5">
          <div id={titleId} className="text-greyscale-headline">
            <RichText>{modal.title}</RichText>
          </div>
          {modal.content ? (
            <div className="text-greyscale-body body-1">
              <RichText interactiveQueryParam={CONTENT_MODAL_QUERY_PARAM}>
                {modal.content}
              </RichText>
            </div>
          ) : null}
        </section>

        {modal.layout === "withHowItWorks" ? <HowItWorksPanel /> : null}

        {modal.footerContent ? (
          <section
            aria-label="Modal footer content"
            className="text-greyscale-body legal flex flex-col gap-3">
            <RichText interactiveQueryParam={CONTENT_MODAL_QUERY_PARAM}>
              {modal.footerContent}
            </RichText>
          </section>
        ) : null}

        <form method="dialog" className="flex justify-center">
          <Button type="submit" secondary className="min-h-11 min-w-52">
            {modal.ctaLabel || "Close"}
          </Button>
        </form>
      </div>
    </Modal>
  );
}

export type ContentModalClientProps = {
  modals: GetContentModalsQueryResult;
};
